import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ListItemButton as MuiListItemButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { AnimatePresence, motion } from 'framer-motion';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// Optimized icon imports
import {
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Groups as GroupsIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  BusinessCenter as ServicesIcon,
  SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import { useAppSelector } from 'app/hooks';
import { LiaAngleDoubleLeftSolid } from 'react-icons/lia';
import { setSelectedService, setSelectedServiceTitle } from 'slices/selectedServiceSlice';
import { AppDispatch } from '../../app/store';
import { useDrawer } from './DrawerContext';
import {
  NavigationItem,
  navigationItems as allNavigationItems,
  canAccessMenuItem,
  filterNavigationByPermissions,
  filterNavigationByServices
} from './MenuItems';
// ADD THIS IMPORT FOR SERVICES FETCH
import { fetchServicesByGroup } from 'slices/NdisSlices/platformAdmin/ManageServicesSlice';

// CONSTANTS FOR LOCALSTORAGE KEYS
const DRAWER_PERSISTENCE_KEYS = {
  EXPANDED_ITEMS: 'drawer_expanded_items',
  SUB_SECTION_EXPANDED: 'drawer_sub_section_expanded',
  SEARCH_TERM: 'drawer_search_term',
  NAVIGATION_ITEMS: 'drawer_navigation_items_cache',
  LAST_SELECTED_SERVICE: 'drawer_last_selected_service',
  USER_PERMISSIONS: 'drawer_user_permissions',
  SERVICES_CACHE: 'drawer_services_cache',
  SERVICES_FETCH_TIMESTAMP: 'drawer_services_fetch_timestamp'
} as const;

// PERSISTENCE HELPER FUNCTIONS
const saveToLocalStorage = (key: string, data: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save to localStorage:', key, error);
  }
};

const loadFromLocalStorage = (key: string, defaultValue: any): any => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn('Failed to load from localStorage:', key, error);
    return defaultValue;
  }
};

// CLEAR OLD DATA ON APP VERSION CHANGE
const clearOldDrawerData = () => {
  const APP_VERSION = '1.0.0';
  const LAST_VERSION_KEY = 'drawer_app_version';

  const lastVersion = localStorage.getItem(LAST_VERSION_KEY);
  if (lastVersion !== APP_VERSION) {
    Object.values(DRAWER_PERSISTENCE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    localStorage.setItem(LAST_VERSION_KEY, APP_VERSION);
  }
};

// CHECK IF SERVICES NEED REFRESH (5 minutes cache)
const shouldRefreshServices = () => {
  const lastFetch = loadFromLocalStorage(DRAWER_PERSISTENCE_KEYS.SERVICES_FETCH_TIMESTAMP, 0);
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  return lastFetch < fiveMinutesAgo;
};

// Styled components - UPDATED with better collapsed drawer support and fixed positioning
const ListItemButton = memo(
  styled(MuiListItemButton)(({ theme }) => {
    const primaryBg = theme.palette.primary.main;
    const faintBg = alpha(theme.palette.primary.main, 0.08);

    return {
      borderRadius: '8px',
      margin: '4px 6px',
      minHeight: '40px',
      transition: 'all 0.2s ease',
      position: 'relative',

      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        transform: 'translateX(2px)'
      },

      // ✅ Direct selected item (solid primary background)
      '&.direct-selected': {
        backgroundColor: primaryBg,
        color: theme.palette.getContrastText(primaryBg),
        boxShadow: `0 2px 8px ${alpha(primaryBg, 0.25)}`,
        transform: 'translateX(0px)',

        '& .MuiListItemIcon-root': {
          color: theme.palette.getContrastText(primaryBg)
        },
        '& .MuiListItemText-primary': {
          fontWeight: 600,
          color: theme.palette.getContrastText(primaryBg),
          fontSize: '0.85rem'
        }
      },

      // ✅ Parent highlighted item (faint background, so text stays dark)
      '&.parent-highlight': {
        backgroundColor: faintBg,
        borderLeft: `3px solid ${primaryBg}`,
        color: theme.palette.text.primary, // force darker text

        '& .MuiListItemIcon-root': {
          color: primaryBg // icons in brand color
        },
        '& .MuiListItemText-primary': {
          fontWeight: 600,
          color: theme.palette.text.primary, // darker text
          fontSize: '0.85rem'
        },
        '&:hover': {
          backgroundColor: alpha(primaryBg, 0.12)
        }
      },

      '&.sub-section': {
        backgroundColor: alpha(theme.palette.grey[100], 0.5),
        marginLeft: '12px',
        marginRight: '12px',
        paddingLeft: theme.spacing(3),
        fontSize: '0.8rem',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        color: theme.palette.text.primary,
        '&:hover': {
          backgroundColor: alpha(primaryBg, 0.03)
        }
      },

      '&.nested-child': {
        paddingLeft: theme.spacing(5),
        minHeight: '36px',
        fontSize: '0.8rem',
        margin: '2px 12px',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
        color: theme.palette.text.primary,
        '&:before': {
          content: '""',
          position: 'absolute',
          left: theme.spacing(3.5),
          top: '50%',
          transform: 'translateY(-50%)',
          width: '8px',
          height: '1px',
          backgroundColor: alpha(primaryBg, 0.3)
        }
      },

      '&.level-separator': {
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        marginTop: '4px',
        paddingTop: '8px'
      },

      '&.collapsed-item': {
        margin: '4px 2px',
        borderRadius: '6px',
        minHeight: '44px',
        width: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        '&:hover': {
          transform: 'none',
          backgroundColor: alpha(primaryBg, 0.08)
        }
      },

      '&.disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
        '&:hover': {
          backgroundColor: 'transparent',
          transform: 'none'
        }
      }
    };
  })
);

// Navigation state interface
interface NavigationState {
  isFromPayroll?: boolean;
  isFromDocument?: boolean;
  previousTab?: string;
  sourceService?: string;
  sourceSection?: string;
}

interface DrawerContentProps {
  isCollapsed?: boolean;
  isMobileDrawer?: boolean;
  onClose?: () => void;
}

export const DrawerContent: React.FC<DrawerContentProps> = memo(
  ({ isCollapsed = false, isMobileDrawer = false, onClose }) => {
    // CLEAR OLD DATA ON COMPONENT MOUNT
    useEffect(() => {
      clearOldDrawerData();
    }, []);

    // Get services from Redux state with fallback to localStorage cache
    const {
      services: reduxServices,
      loading,
      error
    } = useAppSelector((state) => state.manageServices);
    const cachedServices = useMemo(
      () => loadFromLocalStorage(DRAWER_PERSISTENCE_KEYS.SERVICES_CACHE, []),
      []
    );
    const services = reduxServices?.length > 0 ? reduxServices : cachedServices;

    // PERSISTENCE STATE - Load from localStorage with fallbacks
    const [expanded, setExpanded] = useState<string[]>(() =>
      loadFromLocalStorage(DRAWER_PERSISTENCE_KEYS.EXPANDED_ITEMS, [])
    );
    const [subSectionExpanded, setSubSectionExpanded] = useState<string[]>(() =>
      loadFromLocalStorage(DRAWER_PERSISTENCE_KEYS.SUB_SECTION_EXPANDED, [])
    );
    const [search, setSearch] = useState(() =>
      loadFromLocalStorage(DRAWER_PERSISTENCE_KEYS.SEARCH_TERM, '')
    );

    // NON-PERSISTENCE STATE
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
    const [serviceMenuAnchor, setServiceMenuAnchor] = useState<null | HTMLElement>(null);
    const [pendingAction, setPendingAction] = useState<{
      type: 'expand' | 'navigate';
      path: string;
      item?: NavigationItem;
    } | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
      drawerOpen,
      setDrawerOpen,
      drawerWidth,
      collapsedDrawerWidth,
      showAllGroups,
      setShowAllGroups,
      isGroupSelected,
      setIsGroupSelected
    } = useDrawer();

    // UPDATED: Get current state from Redux with localStorage fallbacks
    const { selectedProjectId, isAdmin, selectedProject } = useAppSelector(
      (state) => state.homeTilesServices
    );
    const selectedClient = useAppSelector((state) => state.homeTilesServices?.selectedClient);
    const selectedServiceTitle = useAppSelector(
      (state) => state.serviceSelected.selectedServiceTitle
    );

    // Get additional info from localStorage
    const loginInfo = localStorage.getItem('user-details');
    const userDetails = loginInfo ? JSON.parse(loginInfo) : null;
    const email = userDetails?.email || '';

    // UPDATED: Get project info with fallbacks
    const currentProject =
      selectedProject ||
      loadFromLocalStorage('selectedProject', null) ||
      JSON.parse(localStorage.getItem('selectedProject') || 'null');

    // UPDATED: Get project name from multiple sources
    const projectName =
      currentProject?.projectName ||
      currentProject?.name ||
      loadFromLocalStorage('selectedProjectName', null);

 

    // UPDATED: Only show navigation items if project is selected
    const shouldShowNavigationMenus = Boolean(currentProject || selectedProjectId);

    // UPDATED: FETCH SERVICES ON DRAWER MOUNT IF NEEDED
    useEffect(() => {
      const fetchServices = async () => {
        // Get selectedProjectId from Redux or localStorage fallback
        const projectId =
          selectedProjectId ||
          currentProject?.id ||
          loadFromLocalStorage('selectedProjectId', null);

        // Only fetch if we have required data and services need refresh
        if (projectId && email && shouldRefreshServices()) {
          console.log('🔄 Drawer: Fetching services on mount/refresh', { projectId, email });

          try {
            await dispatch(
              fetchServicesByGroup({
                email,
                group_id: projectId
              })
            ).unwrap();

            // Update fetch timestamp
            saveToLocalStorage(DRAWER_PERSISTENCE_KEYS.SERVICES_FETCH_TIMESTAMP, Date.now());
          } catch (error) {
            console.error('🚨 Drawer: Failed to fetch services:', error);
          }
        } else if (projectId && email) {
          console.log('📋 Drawer: Using cached services (recent fetch)');
        }
      };

      fetchServices();
    }, [dispatch, selectedProjectId, email, currentProject]);

    // CACHE USER PERMISSIONS
    const userPermissions = useMemo(() => {
      const permissions = {
        isPlatformAdmin:
          isAdmin || userDetails?.role?.some((r: any) => r.label.toLowerCase().includes('admin')),
        isServiceAdmin: userDetails?.role?.some((r: any) =>
          ['admin', 'manager', 'supervisor'].includes(r.label.toLowerCase())
        ),
        hasClient: Boolean(selectedClient),
        selectedProjectId
      };

      // Cache permissions
      saveToLocalStorage(DRAWER_PERSISTENCE_KEYS.USER_PERMISSIONS, permissions);
      return permissions;
    }, [isAdmin, userDetails, selectedClient, selectedProjectId]);

    // Use cached permissions if Redux is loading
    const cachedPermissions = loadFromLocalStorage(
      DRAWER_PERSISTENCE_KEYS.USER_PERMISSIONS,
      userPermissions
    );
    const finalPermissions = reduxServices?.length > 0 ? userPermissions : cachedPermissions;

    const { isPlatformAdmin, isServiceAdmin, hasClient } = finalPermissions;

    // Get service info from localStorage for service switching
    const servicesState = JSON.parse(localStorage.getItem('servicesState') || '{}') as {
      selectedServiceTitle?: string;
      selectedService?: { title: string; id: string };
      services?: any[];
    };

    // CURRENT SERVICE with fallback
    const currentService =
      selectedServiceTitle ||
      servicesState.selectedServiceTitle ||
      loadFromLocalStorage(DRAWER_PERSISTENCE_KEYS.LAST_SELECTED_SERVICE, null);

    const shouldShowClientCard = useMemo(() => {
      if (!currentService) return false;
      const serviceString =
        typeof currentService === 'string' ? currentService : String(currentService);
      return serviceString.toLowerCase().includes('ndis');
    }, [currentService]);

    // CACHE SERVICES WHEN THEY LOAD
    useEffect(() => {
      if (reduxServices && reduxServices.length > 0) {
        saveToLocalStorage(DRAWER_PERSISTENCE_KEYS.SERVICES_CACHE, reduxServices);
        console.log('💾 Drawer: Cached services to localStorage', reduxServices.length, 'services');
      }
    }, [reduxServices]);

    // UPDATED: Get filtered navigation items with caching - ONLY if project selected
    const navigationItems = useMemo(() => {
      // If no project selected, return empty array
      if (!shouldShowNavigationMenus) {
        return [];
      }

      let items = [...allNavigationItems];

      // Filter by available services
      items = filterNavigationByServices(items, services || []);

      // Filter by user permissions
      items = filterNavigationByPermissions(items, hasClient, isServiceAdmin, isPlatformAdmin);

      // Cache the final navigation items
      saveToLocalStorage(DRAWER_PERSISTENCE_KEYS.NAVIGATION_ITEMS, items);

      return items;
    }, [services, hasClient, isServiceAdmin, isPlatformAdmin, shouldShowNavigationMenus]);

    // UPDATED: LOAD CACHED NAVIGATION ITEMS IF REDUX IS LOADING - ONLY if project selected
    const cachedNavigationItems = shouldShowNavigationMenus
      ? loadFromLocalStorage(DRAWER_PERSISTENCE_KEYS.NAVIGATION_ITEMS, [])
      : [];
    const finalNavigationItems =
      services?.length > 0 && shouldShowNavigationMenus ? navigationItems : cachedNavigationItems;

    const availableServices = useMemo(() => {
      const services = servicesState?.services;
      return services?.filter((service: any) => service.header !== currentService) || [];
    }, [servicesState, currentService]);

    // PERSISTENCE EFFECTS - Save state changes to localStorage
    useEffect(() => {
      saveToLocalStorage(DRAWER_PERSISTENCE_KEYS.EXPANDED_ITEMS, expanded);
    }, [expanded]);

    useEffect(() => {
      saveToLocalStorage(DRAWER_PERSISTENCE_KEYS.SUB_SECTION_EXPANDED, subSectionExpanded);
    }, [subSectionExpanded]);

    useEffect(() => {
      saveToLocalStorage(DRAWER_PERSISTENCE_KEYS.SEARCH_TERM, search);
    }, [search]);

    useEffect(() => {
      if (currentService) {
        saveToLocalStorage(DRAWER_PERSISTENCE_KEYS.LAST_SELECTED_SERVICE, currentService);
      }
    }, [currentService]);

    // Handle pending actions after drawer opens
    useEffect(() => {
      if (drawerOpen && pendingAction) {
        const timer = setTimeout(() => {
          if (pendingAction.type === 'expand') {
            handleExpand(pendingAction.path);
          } else if (pendingAction.type === 'navigate' && pendingAction.item) {
            handleGo(pendingAction.path, pendingAction.item);
          }
          setPendingAction(null);
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [drawerOpen, pendingAction]);

    // RESTORE EXPANDED STATE FROM CURRENT ROUTE
    useEffect(() => {
      const expandedPaths: string[] = [...expanded];
      const subSectionPaths: string[] = [...subSectionExpanded];

      finalNavigationItems.forEach((item: NavigationItem) => {
        if (location.pathname.startsWith(item.path) && item.children && item.children.length > 0) {
          if (!expandedPaths.includes(item.path)) {
            expandedPaths.push(item.path);
          }

          item.children?.forEach((child: NavigationItem) => {
            if (child.isSubSection && child.children) {
              const hasActiveNestedChild = child.children.some(
                (nested: NavigationItem) => location.pathname === nested.path
              );
              if (
                (hasActiveNestedChild || location.pathname.startsWith(child.path)) &&
                !subSectionPaths.includes(child.path)
              ) {
                subSectionPaths.push(child.path);
              }
            }
          });
        }
      });

      // Only update if there are changes
      if (
        expandedPaths.length !== expanded.length ||
        !expandedPaths.every((path) => expanded.includes(path))
      ) {
        setExpanded(expandedPaths);
      }
      if (
        subSectionPaths.length !== subSectionExpanded.length ||
        !subSectionPaths.every((path) => subSectionExpanded.includes(path))
      ) {
        setSubSectionExpanded(subSectionPaths);
      }
    }, [location.pathname, finalNavigationItems]);

    // Check for child selection and dispatch parent service info
    useEffect(() => {
      finalNavigationItems.forEach((service: any) => {
        const hasChildSelected = service.children?.some((child: any) => {
          return (
            location.pathname === child.path ||
            (child.children &&
              child.children.some((nested: any) => location.pathname === nested.path))
          );
        });

        if (hasChildSelected) {
          let serviceTitle = service.name;
          const serviceId = service.path;

          if (location.pathname.startsWith('/ndis-operations/corporate-services')) {
            serviceTitle = 'NDIS Business Tools';
          }

          dispatch(setSelectedServiceTitle(serviceTitle));
          dispatch(setSelectedService({ title: serviceTitle, id: serviceId }));

          const currentServicesState = JSON.parse(localStorage.getItem('servicesState') || '{}');
          const updatedServicesState = {
            ...currentServicesState,
            selectedServiceTitle: serviceTitle,
            selectedService: {
              title: serviceTitle,
              id: serviceId
            }
          };
          localStorage.setItem('servicesState', JSON.stringify(updatedServicesState));
        }
      });
    }, [location.pathname, finalNavigationItems, dispatch]);

    // Update isGroupSelected based on selectedProjectId
    useEffect(() => {
      if (selectedProjectId || currentProject) {
        setIsGroupSelected(true);
      } else {
        setIsGroupSelected(false);
      }
    }, [selectedProjectId, currentProject, setIsGroupSelected]);

    const handleExpand = useCallback((path: string) => {
      setExpanded((expanded) =>
        expanded.includes(path) ? expanded.filter((x) => x !== path) : [...expanded, path]
      );
    }, []);

    const handleSubSectionExpand = useCallback((path: string) => {
      setSubSectionExpanded((expanded) =>
        expanded.includes(path) ? expanded.filter((x) => x !== path) : [...expanded, path]
      );
    }, []);

    // Enhanced navigation handler with state tracking
    const handleGo = useCallback(
      (path: string, item?: NavigationItem, navigationContext?: any) => {
        if (item && !canAccessMenuItem(item, hasClient, isServiceAdmin, isPlatformAdmin)) {
          if (item.requiresPlatformAdmin && !isPlatformAdmin) {
            setSnackbarMessage('Platform admin access required.');
            return;
          }
          if (item.requiresAdmin && !isServiceAdmin && !isPlatformAdmin) {
            setSnackbarMessage('Admin access required.');
            return;
          }
          if (item.requiresClient && !hasClient) {
            setSnackbarMessage('Please select a client first.');
            return;
          }
        }

        // Determine current context for navigation state
        const currentPath = location.pathname;
        let navState: NavigationState = {};

        const serviceString =
          typeof currentService === 'string' ? currentService : String(currentService || '');

        // Check if coming from services that use payroll routes
        const isPayrollOrScreeningService =
          serviceString.toLowerCase().includes('payroll') ||
          serviceString.toLowerCase().includes('screening') ||
          serviceString.toLowerCase().includes('people');

        if (isPayrollOrScreeningService) {
          navState.sourceService = serviceString.toLowerCase().includes('payroll')
            ? 'payroll'
            : 'people-screening';
          navState.isFromPayroll = serviceString.toLowerCase().includes('payroll');

          if (currentPath.includes('/payroll/people')) {
            navState.previousTab = 'people';
          } else if (currentPath.includes('/payroll/documents')) {
            navState.previousTab = 'documents';
            navState.isFromDocument = true;
          } else if (currentPath.includes('/payroll/onboarding')) {
            navState.previousTab = 'onboarding';
          } else if (currentPath.includes('/ndis-operations/corporate-services')) {
            navState.previousTab = 'clients-organisation';
          }
        }

        // Special handling for corporate services navigation
        if (path === '/ndis-operations/corporate-services' && isPayrollOrScreeningService) {
          navState = {
            isFromPayroll: serviceString.toLowerCase().includes('payroll'),
            isFromDocument: true,
            previousTab: 'clients-organisation',
            sourceService: serviceString.toLowerCase().includes('payroll')
              ? 'payroll'
              : 'people-screening',
            sourceSection: 'organisation'
          };
        }

        navigate(path, { state: navState });

        if (isMobileDrawer && onClose) {
          onClose();
        }
      },
      [
        location.pathname,
        currentService,
        hasClient,
        isServiceAdmin,
        isPlatformAdmin,
        navigate,
        isMobileDrawer,
        onClose
      ]
    );

    // Function to handle opening drawer when collapsed
    const handleOpenDrawer = useCallback(() => {
      setDrawerOpen(true);
    }, [setDrawerOpen]);

    const handleClose = useCallback(() => {
      if (isMobileDrawer && onClose) {
        onClose();
      } else {
        setDrawerOpen(false);
      }
    }, [isMobileDrawer, onClose, setDrawerOpen]);

    const handleServiceSwitch = useCallback(
      (service: any) => {
        const updatedServicesState = {
          ...servicesState,
          selectedServiceTitle: service.header,
          selectedService: {
            title: service.header,
            id: service.service_id
          }
        };
        localStorage.setItem('servicesState', JSON.stringify(updatedServicesState));

        if (service.route) {
          navigate(service.route);
        } else {
          navigate('/services');
        }
        setServiceMenuAnchor(null);
        window.location.reload();
      },
      [servicesState, navigate]
    );

    // Custom component renderer with better collapsed drawer handling
    const renderCustomComponent = (item: NavigationItem) => {
      if (item.customComponent === 'dual-action-buttons') {
        // Better collapsed drawer handling
        if (isCollapsed && !isMobileDrawer) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
                alignItems: 'center',
                py: 1
              }}
            >
              {/* Groups Button - Icon Only for Collapsed */}
              <Tooltip title="Groups" placement="right">
                <IconButton
                  onClick={() => {
                    setShowAllGroups(true);
                    handleGo('/services', item);
                  }}
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: `2px solid ${
                      showAllGroups
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.3)
                    }`,
                    backgroundColor: showAllGroups
                      ? alpha(theme.palette.primary.main, 0.1)
                      : 'transparent',
                    color: showAllGroups
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              {/* Services Button - Icon Only for Collapsed */}
              <Tooltip title="Services" placement="right">
                <IconButton
                  onClick={() => {
                    if (isGroupSelected) {
                      setShowAllGroups(false);
                    }
                    handleGo('/services', item);
                  }}
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: `2px solid ${
                      location.pathname === '/services' && !showAllGroups
                        ? theme.palette.secondary.main
                        : alpha(theme.palette.secondary.main, 0.3)
                    }`,
                    backgroundColor:
                      location.pathname === '/services' && !showAllGroups
                        ? alpha(theme.palette.secondary.main, 0.1)
                        : 'transparent',
                    color:
                      location.pathname === '/services' && !showAllGroups
                        ? theme.palette.secondary.main
                        : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ServicesIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        // Mobile or expanded drawer - original layout
        if (isMobile || isMobileDrawer) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                width: '100%',
                px: 1,
                py: 0.5
              }}
            >
              {/* Groups Button - Icon Only */}
              <Tooltip title="Groups" placement="right">
                <IconButton
                  onClick={() => {
                    setShowAllGroups(true);
                    handleGo('/services', item);
                    if (isMobileDrawer && onClose) onClose();
                  }}
                  sx={{
                    width: '100%',
                    height: '32px',
                    borderRadius: 2,
                    border: `1px solid ${
                      showAllGroups
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.3)
                    }`,
                    backgroundColor: showAllGroups
                      ? alpha(theme.palette.primary.main, 0.1)
                      : 'transparent',
                    color: showAllGroups
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>

              {/* Services Button - Icon Only */}
              <Tooltip title="Services" placement="right">
                <IconButton
                  onClick={() => {
                    if (isGroupSelected) {
                      setShowAllGroups(false);
                    }
                    handleGo('/services', item);
                  }}
                  sx={{
                    width: '100%',
                    height: '32px',
                    borderRadius: 2,
                    border: `1px solid ${
                      location.pathname === '/services' && !showAllGroups
                        ? theme.palette.secondary.main
                        : alpha(theme.palette.secondary.main, 0.3)
                    }`,
                    backgroundColor:
                      location.pathname === '/services' && !showAllGroups
                        ? alpha(theme.palette.secondary.main, 0.1)
                        : 'transparent',
                    color:
                      location.pathname === '/services' && !showAllGroups
                        ? theme.palette.secondary.main
                        : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ServicesIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        // Desktop expanded - show horizontal layout with text
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: '100%',
              px: 1,
              py: 0.5
            }}
          >
            {/* Groups Button - Full Button for Desktop */}
            <Button
              variant={showAllGroups ? 'contained' : 'outlined'}
              size="small"
              startIcon={<GroupsIcon />}
              onClick={() => {
                setShowAllGroups(true);
                handleGo('/services', item);
                if (isMobileDrawer && onClose) onClose();
              }}
              sx={{
                flex: 1,
                fontSize: '0.75rem',
                minHeight: '32px',
                textTransform: 'none',
                ...(showAllGroups
                  ? {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark
                      }
                    }
                  : {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        borderColor: theme.palette.primary.main
                      }
                    })
              }}
            >
              Groups
            </Button>

            {/* Services Button - Full Button for Desktop */}
            <Button
              variant={
                location.pathname === '/services' && !showAllGroups ? 'contained' : 'outlined'
              }
              size="small"
              startIcon={<ServicesIcon />}
              onClick={() => {
                if (isGroupSelected) {
                  setShowAllGroups(false);
                }
                handleGo('/services', item);
              }}
              sx={{
                flex: 1,
                fontSize: '0.75rem',
                minHeight: '32px',
                textTransform: 'none',
                ...(location.pathname === '/services' && !showAllGroups
                  ? {
                      backgroundColor: theme.palette.secondary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.dark
                      }
                    }
                  : {
                      borderColor: alpha(theme.palette.secondary.main, 0.3),
                      color: theme.palette.secondary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                        borderColor: theme.palette.secondary.main
                      }
                    })
              }}
            >
              Services
            </Button>
          </Box>
        );
      }
      return null;
    };

    // Filter items based on search and access permissions with persistence
    const filtered = useMemo(() => {
      const q = search.trim().toLowerCase();
      let items = finalNavigationItems;

      if (q) {
        items = finalNavigationItems
          .map((it: NavigationItem) => {
            const kids = (it.children || []).filter(
              (c: NavigationItem) =>
                c.name.toLowerCase().includes(q) ||
                (c.children &&
                  c.children.some((nested: NavigationItem) =>
                    nested.name.toLowerCase().includes(q)
                  ))
            );
            const par = it.name.toLowerCase().includes(q);
            if (par) return { ...it };
            if (kids.length) return { ...it, children: kids };
            return null;
          })
          .filter(Boolean) as NavigationItem[];
      }

      return items;
    }, [search, finalNavigationItems]);

    const isDirectlySelected = useCallback(
      (path: string) => location.pathname === path,
      [location.pathname]
    );

    const hasChildSelected = useCallback(
      (item: NavigationItem) =>
        item.children?.some(
          (child: NavigationItem) =>
            location.pathname === child.path ||
            (child.children &&
              child.children.some((nested: NavigationItem) => location.pathname === nested.path))
        ) ?? false,
      [location.pathname]
    );

    const getActiveChildrenCount = useCallback(
      (item: NavigationItem) => {
        let count = 0;
        item.children?.forEach((child: NavigationItem) => {
          if (location.pathname === child.path) count++;
          if (child.children) {
            count += child.children.filter(
              (nested: NavigationItem) => location.pathname === nested.path
            ).length;
          }
        });
        return count;
      },
      [location.pathname]
    );

    const isItemAccessible = useCallback(
      (item: NavigationItem) => canAccessMenuItem(item, hasClient, isServiceAdmin, isPlatformAdmin),
      [hasClient, isServiceAdmin, isPlatformAdmin]
    );

    const variants = {
      open: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } },
      closed: { opacity: 0, y: 8, transition: { duration: 0.15 } }
    };

    return (
      <Box
        sx={{
          width: isCollapsed && !isMobileDrawer ? collapsedDrawerWidth : drawerWidth,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: '0 0 20px rgba(0,0,0,0.04)',
          overflow: 'hidden',
          // Add top padding for collapsed drawer to avoid app bar overlap
          ...(isCollapsed &&
            !isMobileDrawer && {
              paddingTop: '64px'
            })
        }}
      >
        {/* Header - Mobile Only */}
        {isMobileDrawer && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              minHeight: '56px'
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main
                  }}
                >
                  Menu
                </Typography>
              </Box>
            </motion.div>

            <motion.div>
              <Box>
                <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}>
                  <Tooltip title="Close Menu" placement="left">
                    <IconButton
                      onClick={handleClose}
                      size="small"
                      sx={{
                        p: 1,
                        '&:hover': {
                          transform: 'rotate(90deg)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </motion.div>
              </Box>
            </motion.div>
          </Box>
        )}

        {/* UPDATED: Service Status and Switcher - Hide for collapsed drawer */}
        {(!isCollapsed || isMobileDrawer) && (
          <Box
            sx={{
              px: 2,
              pb: shouldShowClientCard ? 1 : 0,
              pt: isMobileDrawer ? 1 : 10
            }}
          >
            <Stack spacing={1}>
              {/* UPDATED: Current Group/Team - with better fallbacks */}
              {currentProject && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={<GroupsIcon />}
                    label={currentProject}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      flex: 1,
                      height: '28px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        px: 1
                      },
                      '& .MuiChip-icon': {
                        fontSize: '16px'
                      }
                    }}
                  />
                </Box>
              )}

              {/* Current Service */}
              {currentProject && currentService && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={<BusinessIcon />}
                    label={String(currentService)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      flex: 1,
                      height: '28px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        px: 1
                      },
                      '& .MuiChip-icon': {
                        fontSize: '16px'
                      }
                    }}
                  />
                  {availableServices && availableServices.length > 0 && (
                    <Tooltip title="Switch Service">
                      <IconButton
                        size="small"
                        onClick={(e) => setServiceMenuAnchor(e.currentTarget)}
                        sx={{ p: 0.5 }}
                      >
                        <SwapHorizIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}

              {/* Client Status */}
              {shouldShowClientCard && (
                <Box>
                  {hasClient ? (
                    <Chip
                      icon={<PersonIcon />}
                      label={`Client: ${selectedClient?.label}`}
                      size="small"
                      color="success"
                      sx={{
                        fontSize: '0.75rem',
                        height: '28px',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          px: 1
                        },
                        '& .MuiChip-icon': {
                          fontSize: '16px'
                        }
                      }}
                    />
                  ) : (
                    <Alert
                      severity="warning"
                      sx={{ py: 0.5, fontSize: '0.75rem', '& .MuiAlert-message': { py: 0.5 } }}
                    >
                      No client selected
                    </Alert>
                  )}
                </Box>
              )}
            </Stack>
          </Box>
        )}

        {/* Search Box - Hide for collapsed drawer */}
        {(!isCollapsed || isMobileDrawer) && (
          <Box sx={{ p: 1, pt: shouldShowClientCard ? 0 : 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search menu..."
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    fontSize: '0.85rem',
                    '& input': {
                      fontSize: '0.85rem',
                      py: 1
                    },
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.25)
                    },
                    '&.Mui-focused': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.08)}`
                    }
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
              {!isMobileDrawer && (
                <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}>
                  <Tooltip title="Collapse Menu" placement="left">
                    <IconButton
                      onClick={handleClose}
                      size="small"
                      sx={{
                        p: 1,
                        '&:hover': {
                          transform: 'translateX(-2px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <LiaAngleDoubleLeftSolid />
                    </IconButton>
                  </Tooltip>
                </motion.div>
              )}
            </Stack>
          </Box>
        )}

        {(!isCollapsed || isMobileDrawer) && (
          <Divider sx={{ bgcolor: alpha(theme.palette.divider, 0.05) }} />
        )}

        {/* UPDATED: Menu list */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            ...(isCollapsed &&
              !isMobileDrawer && {
                paddingTop: '8px'
              })
          }}
        >
          <List
            sx={{
              px: isCollapsed && !isMobileDrawer ? 0.25 : 1,
              py: isCollapsed && !isMobileDrawer ? 0 : 0
            }}
          >
            {/* UPDATED: Navigation Items - only show if project selected OR show Groups/Services buttons */}
            {shouldShowNavigationMenus ? (
              // Show full navigation when project is selected
              filtered.map((item: NavigationItem, index: any) => {
                const directlySelected = isDirectlySelected(item.path);
                const hasActiveChild = hasChildSelected(item);
                const activeChildrenCount = getActiveChildrenCount(item);
                const isAccessible = isItemAccessible(item);
                const hasChildren = item.children?.length && item.children?.length > 0;

                // Check for custom components (dual buttons)
                if (item.customComponent) {
                  return (
                    <motion.div
                      key={item.path}
                      variants={variants}
                      initial="closed"
                      animate="open"
                      exit="closed"
                    >
                      <ListItem
                        disablePadding
                        sx={{
                          mb: isCollapsed && !isMobileDrawer ? 0 : 0.5,
                          justifyContent: isCollapsed && !isMobileDrawer ? 'center' : 'flex-start'
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            px: isCollapsed && !isMobileDrawer ? 0 : 1,
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          {/* Render custom component */}
                          {renderCustomComponent(item)}
                        </Box>
                      </ListItem>
                    </motion.div>
                  );
                }

                return (
                  <motion.div
                    key={item.path}
                    variants={variants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                  >
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <Tooltip
                        title={
                          isCollapsed && !isMobileDrawer
                            ? item.name
                            : !isAccessible
                            ? 'Restricted'
                            : ''
                        }
                        placement="right"
                        arrow
                      >
                        <ListItemButton
                          onClick={() => {
                            if (!isAccessible) {
                              if (item.requiresPlatformAdmin && !isPlatformAdmin) {
                                setSnackbarMessage('Platform admin access required.');
                              } else if (
                                item.requiresAdmin &&
                                !isServiceAdmin &&
                                !isPlatformAdmin
                              ) {
                                setSnackbarMessage('Admin access required.');
                              } else if (item.requiresClient && !hasClient) {
                                setSnackbarMessage('Please select a client first.');
                              }
                              return;
                            }

                            // If collapsed, open drawer AND queue the action to execute after drawer opens
                            if (isCollapsed && !isMobileDrawer) {
                              handleOpenDrawer();
                              // Navigate to parent route
                              handleGo(item.path, item);
                              // Queue the expand action
                              if (hasChildren) {
                                setPendingAction({ type: 'expand', path: item.path });
                              } else {
                                setPendingAction({ type: 'navigate', path: item.path, item });
                              }
                              return;
                            }

                            // UPDATED: Navigate to parent AND expand children
                            if (hasChildren) {
                              handleGo(item.path, item); // Navigate to parent route
                              handleExpand(item.path); // Also expand to show children
                            } else {
                              handleGo(item.path, item);
                            }
                          }}
                          className={[
                            directlySelected ? 'direct-selected' : '',
                            hasActiveChild ? 'parent-highlight' : '',
                            !isAccessible ? 'disabled' : '',
                            index > 0 ? 'level-separator' : '',
                            isCollapsed && !isMobileDrawer ? 'collapsed-item' : ''
                          ]
                            .filter(Boolean)
                            .join(' ')
                            .trim()}
                          sx={{
                            justifyContent:
                              isCollapsed && !isMobileDrawer ? 'center' : 'flex-start',
                            ...(isCollapsed &&
                              !isMobileDrawer && {
                                margin: '4px auto',
                                width: '44px',
                                height: '44px',
                                borderRadius: '8px',
                                minHeight: '44px'
                              })
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: isCollapsed && !isMobileDrawer ? 0 : 40,
                              justifyContent: 'center',
                              color: directlySelected
                                ? '#fff'
                                : hasActiveChild
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary
                            }}
                          >
                            {!isAccessible ? (
                              <Box sx={{ position: 'relative' }}>
                                {React.cloneElement(item.icon! as React.ReactElement<any>, { sx: { fontSize: 20 } })}
                                <LockIcon
                                  sx={{
                                    position: 'absolute',
                                    bottom: -1,
                                    right: -1,
                                    fontSize: 10,
                                    color: theme.palette.error.main,
                                    bgcolor: 'background.paper',
                                    borderRadius: '50%'
                                  }}
                                />
                              </Box>
                            ) : isCollapsed && !isMobileDrawer ? (
                              hasActiveChild && !directlySelected ? (
                                <Badge
                                  badgeContent={activeChildrenCount}
                                  color="primary"
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.65rem',
                                      minWidth: '16px',
                                      height: '16px',
                                      fontWeight: 600
                                    }
                                  }}
                                >
                                  {React.cloneElement(item.icon! as React.ReactElement<any>, { sx: { fontSize: 20 } })}
                                </Badge>
                              ) : (
                                React.cloneElement(item.icon! as React.ReactElement<any>, { sx: { fontSize: 20 } })
                              )
                            ) : (
                              React.cloneElement(item.icon! as React.ReactElement<any>, { sx: { fontSize: 20 } })
                            )}
                          </ListItemIcon>

                          {/* Only show text when not collapsed */}
                          {(!isCollapsed || isMobileDrawer) && (
                            <>
                              <ListItemText
                                primary={item.name}
                                primaryTypographyProps={{
                                  fontWeight: directlySelected ? 600 : hasActiveChild ? 600 : 400,
                                  fontSize: '0.85rem',
                                  color: !isAccessible
                                    ? theme.palette.text.disabled
                                    : hasActiveChild && !directlySelected
                                    ? theme.palette.primary.main
                                    : undefined,
                                  noWrap: true
                                }}
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              />

                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexShrink: 0
                                }}
                              >
                                {!isAccessible && (
                                  <LockIcon
                                    sx={{ fontSize: 14, color: theme.palette.error.main }}
                                  />
                                )}
                                {hasActiveChild && !directlySelected && isAccessible && (
                                  <FiberManualRecordIcon
                                    sx={{
                                      fontSize: 8,
                                      color: theme.palette.primary.main,
                                      animation: 'pulse 2s infinite'
                                    }}
                                  />
                                )}
                                {hasChildren && (
                                  <ChevronRightIcon
                                    sx={{
                                      transform: expanded.includes(item.path)
                                        ? 'rotate(90deg)'
                                        : 'rotate(0deg)',
                                      fontSize: 18,
                                      color: alpha(theme.palette.text.primary, 0.4),
                                      transition: 'transform 0.2s ease'
                                    }}
                                  />
                                )}
                              </Box>
                            </>
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>

                    {/* Children - Only show when not collapsed */}
                    <AnimatePresence>
                      {expanded.includes(item.path) && (!isCollapsed || isMobileDrawer) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          style={{ overflow: 'hidden' }}
                        >
                          {item.children?.map((child: NavigationItem, childIndex) => {
                            const isChildAccessible = isItemAccessible(child);
                            const isChildSelected = isDirectlySelected(child.path);

                            return (
                              <motion.div
                                key={child.path}
                                variants={variants}
                                initial="closed"
                                animate="open"
                                exit="closed"
                                style={{ marginLeft: '12px' }}
                              >
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={() => {
                                      const navContext = {
                                        fromService: currentService,
                                        fromSection: item.name
                                      };

                                      if (child.isSubSection && child.children) {
                                        handleSubSectionExpand(child.path);
                                        handleGo(child.path, child, navContext);
                                      } else {
                                        handleGo(child.path, child, navContext);
                                      }
                                    }}
                                    className={[
                                      isChildSelected ? 'direct-selected' : '',
                                      child.isSubSection ? 'sub-section' : '',
                                      !isChildAccessible ? 'disabled' : '',
                                      childIndex > 0 ? 'level-separator' : ''
                                    ]
                                      .filter(Boolean)
                                      .join(' ')
                                      .trim()}
                                    sx={{
                                      pl: child.isSubSection ? 3 : 3.5,
                                      py: 0.75,
                                      mb: 0.25
                                    }}
                                  >
                                    {child.icon && (
                                      <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                        {!isChildAccessible ? (
                                          <Box sx={{ position: 'relative' }}>
                                            {React.cloneElement(child.icon as React.ReactElement<any>, {
                                              sx: { fontSize: 18 }
                                            })}
                                            <LockIcon
                                              sx={{
                                                position: 'absolute',
                                                bottom: -1,
                                                right: -1,
                                                fontSize: 8,
                                                color: theme.palette.error.main,
                                                bgcolor: 'background.paper',
                                                borderRadius: '50%'
                                              }}
                                            />
                                          </Box>
                                        ) : (
                                          React.cloneElement(child.icon as React.ReactElement<any>, { sx: { fontSize: 18 } })
                                        )}
                                      </ListItemIcon>
                                    )}
                                    <ListItemText
                                      primary={child.name}
                                      primaryTypographyProps={{
                                        fontWeight: isChildSelected ? 600 : 400,
                                        fontSize: '0.8rem',
                                        color: !isChildAccessible
                                          ? theme.palette.text.disabled
                                          : isChildSelected
                                          ? '#fff'
                                          : undefined
                                      }}
                                    />
                                    {!isChildAccessible && (
                                      <LockIcon
                                        sx={{ fontSize: 12, color: theme.palette.error.main }}
                                      />
                                    )}
                                    {child.isSubSection && child.children && (
                                      <ChevronRightIcon
                                        sx={{
                                          transform: subSectionExpanded.includes(child.path)
                                            ? 'rotate(90deg)'
                                            : 'rotate(0deg)',
                                          fontSize: 16,
                                          color: alpha(theme.palette.text.primary, 0.4),
                                          transition: 'transform 0.2s ease'
                                        }}
                                      />
                                    )}
                                  </ListItemButton>
                                </ListItem>

                                {/* Nested children for sub-sections */}
                                {child.isSubSection && child.children && (
                                  <Collapse
                                    in={subSectionExpanded.includes(child.path)}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    {child.children.map(
                                      (nestedChild: NavigationItem, nestedIndex) => {
                                        const isNestedAccessible = isItemAccessible(nestedChild);
                                        const isNestedSelected = isDirectlySelected(
                                          nestedChild.path
                                        );

                                        return (
                                          <motion.div
                                            key={nestedChild.path}
                                            variants={variants}
                                            initial="closed"
                                            animate="open"
                                            exit="closed"
                                          >
                                            <ListItem disablePadding>
                                              <ListItemButton
                                                onClick={() => {
                                                  const navContext = {
                                                    fromService: currentService,
                                                    fromSection: child.name,
                                                    parentSection: item.name
                                                  };
                                                  handleGo(
                                                    nestedChild.path,
                                                    nestedChild,
                                                    navContext
                                                  );
                                                }}
                                                className={[
                                                  isNestedSelected ? 'direct-selected' : '',
                                                  !isNestedAccessible ? 'disabled' : '',
                                                  'nested-child',
                                                  nestedIndex > 0 ? 'level-separator' : ''
                                                ]
                                                  .filter(Boolean)
                                                  .join(' ')
                                                  .trim()}
                                                sx={{ mb: 0.25 }}
                                              >
                                                {nestedChild.icon && (
                                                  <ListItemIcon sx={{ minWidth: 28, mr: 1 }}>
                                                    {!isNestedAccessible ? (
                                                      <Box sx={{ position: 'relative' }}>
                                                        {React.cloneElement(nestedChild.icon as React.ReactElement<any>, {
                                                          sx: { fontSize: 16 }
                                                        })}
                                                        <LockIcon
                                                          sx={{
                                                            position: 'absolute',
                                                            bottom: -1,
                                                            right: -1,
                                                            fontSize: 7,
                                                            color: theme.palette.error.main,
                                                            bgcolor: 'background.paper',
                                                            borderRadius: '50%'
                                                          }}
                                                        />
                                                      </Box>
                                                    ) : (
                                                      React.cloneElement(nestedChild.icon as React.ReactElement<any>, {
                                                        sx: { fontSize: 16 }
                                                      })
                                                    )}
                                                  </ListItemIcon>
                                                )}
                                                <ListItemText
                                                  primary={nestedChild.name}
                                                  primaryTypographyProps={{
                                                    fontWeight: isNestedSelected ? 600 : 400,
                                                    fontSize: '0.75rem',
                                                    color: !isNestedAccessible
                                                      ? theme.palette.text.disabled
                                                      : isNestedSelected
                                                      ? '#fff'
                                                      : undefined
                                                  }}
                                                />
                                                {!isNestedAccessible && (
                                                  <LockIcon
                                                    sx={{
                                                      fontSize: 11,
                                                      color: theme.palette.error.main
                                                    }}
                                                  />
                                                )}
                                              </ListItemButton>
                                            </ListItem>
                                          </motion.div>
                                        );
                                      }
                                    )}
                                  </Collapse>
                                )}
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })
            ) : (
              // UPDATED: Show only Groups/Services buttons when no project selected
              <motion.div variants={variants} initial="closed" animate="open" exit="closed">
                <ListItem
                  disablePadding
                  sx={{
                    mb: isCollapsed && !isMobileDrawer ? 0 : 0.5,
                    justifyContent: isCollapsed && !isMobileDrawer ? 'center' : 'flex-start'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      px: isCollapsed && !isMobileDrawer ? 0 : 1,
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    {/* Show Groups/Services buttons even without project */}
                    {renderCustomComponent({
                      customComponent: 'dual-action-buttons',
                      path: '/services',
                      name: 'Services',
                      icon: <ServicesIcon />
                    } as NavigationItem)}
                  </Box>
                </ListItem>
              </motion.div>
            )}

            {/* UPDATED: No Results - show different message based on context */}
            {shouldShowNavigationMenus && filtered.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ opacity: 0.7, fontSize: '0.8rem' }}
                >
                  {search ? 'No menu items found for search' : 'No menu items available'}
                </Typography>
              </Box>
            )}
          </List>
        </Box>

        {/* Service Switcher Menu */}
        <Menu
          anchorEl={serviceMenuAnchor}
          open={Boolean(serviceMenuAnchor)}
          onClose={() => setServiceMenuAnchor(null)}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 200,
              maxWidth: 300,
              boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
            }
          }}
        >
          <Box
            sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Switch Service
            </Typography>
          </Box>
          {availableServices &&
            availableServices.map((service: any) => (
              <MenuItem
                key={service.service_id}
                onClick={() => handleServiceSwitch(service)}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {service.icon ? (
                    <img src={service.icon} alt={service.header} width="22" height="22" />
                  ) : (
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                    >
                      {service.header.charAt(0)}
                    </Box>
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={service.header}
                  secondary={service.description}
                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
            ))}
        </Menu>

        {/* Error Messages */}
        {snackbarMessage && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="warning"
              onClose={() => setSnackbarMessage(null)}
              sx={{
                fontSize: '0.75rem'
              }}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}
      </Box>
    );
  }
);

DrawerContent.displayName = 'DrawerContent';
